/* Ember cart service — the single path for storefront cart writes.
 *
 * Plan: docs/plans/2026-10-14-001-feat-gift-engine-cart-service.md (§4.1–4.4).
 * Shipped to the theme as assets/cart-service.js (plain script →
 * window.EmberCart). Requires assets/gift-engine.js (window.EmberGiftEngine).
 *
 * What it guarantees:
 *  1. ONE write at a time — every cart write (ours and the theme's) is queued.
 *  2. Lines are resolved against the LIVE cart when the write is sent
 *     (key → variant + properties → sole line of that variant), never from a
 *     position/key rendered earlier (Shopify re-keys lines when a gift is added).
 *  3. Gifts travel WITH the item: an add that earns a tier carries the gift line
 *     in the same /cart/add.js, so the drawer renders once, gift included.
 *  4. Quantity/remove changes return a final cart (+ sections) that already
 *     reflects the gift swap, so callers render once.
 *  5. Writes we don't own (other scripts/apps) are followed by a reconcile pass
 *     that re-reads the cart and fixes gifts.
 * Pricing is never decided here: the gift-tiers-discount function frees gifts.
 */
(function () {
  'use strict';
  if (window.EmberCart) return;
  var E = window.EmberGiftEngine;
  if (!E || !window.fetch) return; // engine missing → leave the theme untouched

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var OWN = 'X-Ember-Cart';          // marks requests this service sends itself
  var nativeFetch = window.fetch.bind(window);
  var queue = Promise.resolve();
  var lastCart = null;
  var reconcileTimer = null;

  function enqueue(task) {
    var run = queue.then(task, task);
    queue = run.catch(function () {});
    return run;
  }
  function headers(json) {
    var h = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (json) h['Content-Type'] = 'application/json';
    h[OWN] = '1';
    return h;
  }
  function parse(res) {
    return res.text().then(function (t) {
      var body = {};
      try { body = t ? JSON.parse(t) : {}; } catch (e) { body = { status: res.status, description: t }; }
      return { ok: res.ok, status: res.status, body: body };
    });
  }
  function post(path, payload) {
    return nativeFetch(ROOT + path, { method: 'POST', headers: headers(true), body: JSON.stringify(payload) }).then(parse);
  }
  function getCart() {
    return nativeFetch(ROOT + 'cart.js', { headers: headers(false) }).then(parse).then(function (r) {
      if (r.ok && Array.isArray(r.body.items)) lastCart = r.body;
      return r.body;
    });
  }
  function readConfig() {
    var el = document.querySelector('[data-spend-gift-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function isCartWrite(url) { return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url); }
  function isAdd(url) { return /\/cart\/add(\.js)?(\?|$)/.test(url); }
  function pathOf(input) {
    var u = String((input && input.url) || input || '');
    try { return new URL(u, location.href).pathname; } catch (e) { return u; }
  }

  /* ---------- line resolution ---------- */
  function canonProps(o) {
    var src = o || {};
    return JSON.stringify(Object.keys(src).sort().reduce(function (a, k) { a[k] = src[k]; return a; }, {}));
  }
  /* ref: { key, variantId, props (object|JSON string|undefined), gift (bool) } */
  function resolveLine(cart, ref) {
    var items = (cart && cart.items) || [];
    if (ref.key) for (var i = 0; i < items.length; i++) if (items[i].key === ref.key) return items[i];
    if (!ref.variantId) return null;
    var same = items.filter(function (it) { return String(it.variant_id) === String(ref.variantId); });
    if (ref.props != null) {
      var want = null;
      try { want = canonProps(typeof ref.props === 'string' ? JSON.parse(ref.props) : ref.props); } catch (e) {}
      if (want !== null) {
        var exact = same.filter(function (it) { return canonProps(it.properties) === want; });
        if (exact.length) return exact[0];
      }
    }
    // Without properties: prefer non-gift lines (gift rows have no qty controls).
    var paid = same.filter(function (it) { return !E.isGiftLine(it); });
    if (paid.length === 1) return paid[0];
    return same.length === 1 ? same[0] : null;
  }

  /* ---------- gift application ---------- */
  /* Bring `cart` in line with the gift engine. Returns the final cart (with
   * `sections` when requested), or null when nothing needed to change. */
  function applyGiftPlan(cart, sections, sectionsUrl) {
    var cfg = readConfig();
    if (!cfg) return Promise.resolve(null);
    var plan = E.planTierGifts(cart, cfg);
    // Product gift (separate offer): one free unit per distinct listed product.
    var pplan = E.planProductGifts ? E.planProductGifts(cart, cfg) : { add: 0, updates: {} };
    if (E.isNoop(plan) && (!E.isProductNoop || E.isProductNoop(pplan))) return Promise.resolve(null);
    var step = Promise.resolve();
    var adds = plan.add.map(E.giftLineItem);
    if (pplan.add) adds.push(E.productGiftItem(pplan.add, cfg));
    if (adds.length) {
      step = post('cart/add.js', { items: adds });
    }
    return step.then(function () {
      var updates = {};
      plan.remove.forEach(function (k) { updates[k] = 0; });
      plan.clamp.forEach(function (k) { updates[k] = 1; });
      Object.keys(pplan.updates).forEach(function (k) { updates[k] = pplan.updates[k]; });
      var payload = { updates: updates };
      if (sections && sections.length) { payload.sections = sections; payload.sections_url = sectionsUrl || location.pathname; }
      return post('cart/update.js', payload);
    }).then(function (r) {
      if (r.ok && Array.isArray(r.body.items)) lastCart = r.body;
      return r.ok ? r.body : null;
    });
  }

  /* ---------- add: gifts in the same request ---------- */
  var IGNORED_FORM_KEYS = /^(sections|sections_url|form_type|utf8|product-id|section-id)$/;
  /* FormData → items[] (single product form). null = shape we don't recognise,
   * in which case the request is sent untouched. */
  function itemsFromFormData(fd) {
    var item = { properties: {} }, ok = false;
    var bad = false;
    fd.forEach(function (value, key) {
      if (key === 'id') { item.id = parseInt(value, 10); ok = true; }
      else if (key === 'quantity') item.quantity = parseInt(value, 10) || 1;
      else if (key === 'selling_plan') { if (value) item.selling_plan = value; }
      else if (/^properties\[(.+)\]$/.test(key)) { if (value !== '') item.properties[key.match(/^properties\[(.+)\]$/)[1]] = value; }
      else if (!IGNORED_FORM_KEYS.test(key)) bad = true;
    });
    if (!ok || bad || !(item.id > 0)) return null;
    if (!item.quantity) item.quantity = 1;
    if (!Object.keys(item.properties).length) delete item.properties;
    return { items: [item], sections: fd.get('sections'), sections_url: fd.get('sections_url') };
  }
  function itemsFromJson(text) {
    var b;
    try { b = JSON.parse(text); } catch (e) { return null; }
    if (Array.isArray(b.items)) return { items: b.items.slice(), sections: b.sections, sections_url: b.sections_url };
    if (b.id) return { items: [{ id: b.id, quantity: b.quantity || 1, properties: b.properties }], sections: b.sections, sections_url: b.sections_url };
    return null;
  }
  /* Unit price (paise) of a variant: a looked-up price, the page's product
   * data, else a "Mostly paired with" add-on card. NaN = unknown → no
   * in-request gift (the reconciler adds it after, in a second request). */
  var priceCache = {};
  var availCache = {}; // variant id → true/false, from the same /variants/<id>.js lookup
  function unitPrice(variantId) {
    if (priceCache[variantId] >= 0) return priceCache[variantId];
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    var vs = (meta && meta.variants) || [];
    for (var i = 0; i < vs.length; i++) if (String(vs[i].id) === String(variantId) && typeof vs[i].price === 'number') return vs[i].price;
    var card = document.querySelector('[data-addon-card][data-addon-variant-id="' + variantId + '"]');
    var p = card ? parseInt(card.dataset.addonPrice, 10) : NaN;
    return p >= 0 ? p : NaN;
  }
  /* Price of a variant that isn't on this page (collection cards, quick add,
   * recommendations, upsells): GET /variants/<id>.js (~0.25s). Started on
   * hover/touch of an add button so it's usually cached before the click;
   * capped at LOOKUP_MS so a slow lookup never holds the add back much. */
  var LOOKUP_MS = 1500;
  var inflight = {};
  function lookupPrice(variantId) {
    var id = String(variantId);
    if (!(parseInt(id, 10) > 0) || (unitPrice(id) >= 0 && id in availCache)) return Promise.resolve();
    if (!inflight[id]) {
      inflight[id] = nativeFetch(ROOT + 'variants/' + id + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (v) {
          if (v && typeof v.price === 'number') priceCache[id] = v.price;
          if (v && typeof v.available === 'boolean') availCache[id] = v.available;
        })
        .catch(function () {})
        .then(function () { delete inflight[id]; });
    }
    return Promise.race([inflight[id], new Promise(function (res) { setTimeout(res, LOOKUP_MS); })]);
  }
  function lookupPrices(parsed) {
    if (!parsed) return Promise.resolve();
    var cfg = readConfig();
    var jobs = parsed.items.map(function (it) {
      return (it.properties && E.isGiftLine(it)) || (E.isExcluded && E.isExcluded(it.id, null, cfg)) ? null : lookupPrice(it.id);
    });
    // Product gift's stock status, so it only rides in the shopper's add when available.
    if (cfg && cfg.productGift && cfg.productGift.variantId) jobs.push(lookupPrice(cfg.productGift.variantId));
    return Promise.all(jobs);
  }
  function prefetchFromEvent(ev) {
    var t = ev.target && ev.target.closest && ev.target.closest('form[action*="/cart/add"]');
    var input = t && t.querySelector('[name="id"]');
    if (input && input.value) lookupPrice(input.value);
  }
  ['pointerover', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(type, prefetchFromEvent, { capture: true, passive: true });
  });

  function addingPaise(items, cfg) {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.properties && E.isGiftLine(it)) continue;
      if (E.isExcluded && E.isExcluded(it.id, null, cfg)) continue; // never counts toward a tier
      var u = unitPrice(it.id);
      if (!(u >= 0)) return NaN;
      total += u * (parseInt(it.quantity, 10) || 1);
    }
    return total;
  }
  /* Returns a replacement fetch init carrying the gift(s), or null to send the
   * original request unchanged. */
  function parseAdd(init) {
    if (!init) return null;
    var parsed = null;
    if (typeof FormData !== 'undefined' && init.body instanceof FormData) parsed = itemsFromFormData(init.body);
    else if (typeof init.body === 'string') parsed = itemsFromJson(init.body);
    return parsed && parsed.items.length ? parsed : null;
  }
  function withGifts(init, parsed, cartBefore) {
    var cfg = readConfig();
    if (!cfg || !parsed || !cartBefore) return null;
    var paise = addingPaise(parsed.items, cfg);
    if (!(paise >= 0)) return null;
    var gifts = E.giftsForAdd(cartBefore, paise, cfg, parsed.items);
    // Product gift for listed products being added — only when known in stock (an
    // unavailable gift would make the shopper's own add fail with a 422).
    if (E.productGiftsForAdd && cfg.productGift && availCache[String(cfg.productGift.variantId)] === true) {
      gifts = gifts.concat(E.productGiftsForAdd(cartBefore, parsed.items, cfg));
    }
    if (!gifts.length) return null;
    var body = { items: parsed.items.concat(gifts) };
    if (parsed.sections) {
      body.sections = Array.isArray(parsed.sections) ? parsed.sections : String(parsed.sections).split(',');
      body.sections_url = parsed.sections_url || location.pathname;
    }
    var h = {};
    var src = init.headers || {};
    if (typeof Headers !== 'undefined' && src instanceof Headers) src.forEach(function (v, k) { h[k] = v; });
    else for (var k in src) h[k] = src[k];
    h['Content-Type'] = 'application/json';
    h.Accept = 'application/json';
    return Object.assign({}, init, { method: 'POST', headers: h, body: JSON.stringify(body) });
  }

  /* ---------- transport middleware ---------- */
  window.fetch = function (input, init) {
    var path = pathOf(input);
    var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    var own = init && init.headers && ((init.headers[OWN]) || (typeof Headers !== 'undefined' && init.headers instanceof Headers && init.headers.get(OWN)));
    if (method !== 'POST' || own || !isCartWrite(path)) return nativeFetch(input, init);

    // A theme/app cart write: serialise it behind any running write, add the
    // gift to adds that earn one, then re-check gifts afterwards.
    return enqueue(function () {
      var parsed = isAdd(path) && readConfig() ? parseAdd(init) : null;
      // Cart + any unknown prices are fetched in parallel.
      var before = parsed ? Promise.all([lastCart ? lastCart : getCart(), lookupPrices(parsed)]) : Promise.resolve([null]);
      return before.then(function (r) {
        var next = parsed ? withGifts(init, parsed, r[0]) : null;
        return nativeFetch(input, next || init).then(function (res) {
          if (res.ok) scheduleReconcile(next ? 50 : 0);
          return res;
        });
      });
    });
  };

  /* ---------- change: quantity / remove ---------- */
  /* ref as resolveLine; returns the final cart JSON (+ sections) — gift swap
   * already applied — or an { errors } object like /cart/change.js. */
  function change(ref, quantity, opts) {
    opts = opts || {};
    return enqueue(function () {
      return getCart().then(function (cart) {
        var line = resolveLine(cart, ref);
        var sections = opts.sections || [];
        if (!line) {
          // Already gone (e.g. removed elsewhere) — return a fresh render, no error.
          return post('cart/update.js', { updates: {}, sections: sections, sections_url: opts.sections_url || location.pathname })
            .then(function (r) { if (r.ok) lastCart = r.body; return r.body; });
        }
        return post('cart/change.js', { id: line.key, quantity: quantity, sections: sections, sections_url: opts.sections_url || location.pathname })
          .then(function (r) {
            if (!r.ok) return r.body.errors ? r.body : { errors: r.body.description || r.body.message || 'error' };
            lastCart = r.body;
            return applyGiftPlan(r.body, sections, opts.sections_url).then(function (fin) { return fin || r.body; });
          });
      });
    });
  }

  /* ---------- reconcile: safety net ---------- */
  function scheduleReconcile(delay) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () { reconcile(); }, delay || 0);
  }
  function drawerSections() {
    var d = document.querySelector('cart-drawer');
    return d && d.getSectionsToRender ? d.getSectionsToRender().map(function (s) { return s.id; }) : [];
  }
  function reconcile() {
    return enqueue(function () {
      return getCart().then(function (cart) {
        return applyGiftPlan(cart, drawerSections(), location.pathname).then(function (fin) {
          if (!fin) return null;
          // Gifts changed outside a caller that renders: let the theme re-render
          // (drawer/cart page subscribers refresh on any non-'cart-items' source).
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ember-cart', cartData: fin });
          }
          var bubble = document.querySelector('.cart-count-bubble span, .cart-count-bubble');
          if (bubble && typeof fin.item_count === 'number') bubble.textContent = fin.item_count;
          return fin;
        });
      });
    });
  }

  // Tab refocus (another tab may have changed the cart) and first load.
  document.addEventListener('visibilitychange', function () { if (!document.hidden) scheduleReconcile(0); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scheduleReconcile(0); });
  else scheduleReconcile(0);

  // Product gift's stock status, fetched up front so adds and Buy Now can include
  // the gift only when it is known to be available.
  (function () {
    var cfg = readConfig();
    if (cfg && cfg.productGift && cfg.productGift.variantId) lookupPrice(cfg.productGift.variantId);
  })();

  window.EmberCart = {
    change: change,
    reconcile: reconcile,
    resolveLine: resolveLine,
    getCart: getCart,
    _itemsFromFormData: itemsFromFormData,
    /* true / false once known, undefined before the lookup lands. */
    isAvailable: function (variantId) { return availCache[String(variantId)]; },
  };
})();
